import { useEntity } from '@backstage/plugin-catalog-react';

import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';

import _ from 'lodash';

import { EditCommonRolesIcon } from './CommonRolesEditIcon';
import { useStyles } from '../styles';
import { CpeCardProps, Features, Sso } from '../types';

export function CommonRolesCard(props: CpeCardProps) {
  const { variant } = props;
  const classes = useStyles();
  const { entity } = useEntity();

  const features: Features | undefined = entity?.spec?.features as
    | Features
    | undefined;

  const commonRoles: Sso | undefined = features?.sso as Sso | undefined;

  let cardClass = '';
  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
    cardContentClass = classes.fullHeightCardContent;
  }

  return (
    <Card className={cardClass}>
      <CardHeader
        title="Common Roles"
        variant={variant}
        action={
          <EditCommonRolesIcon
            entity={entity}
            commonRoles={commonRoles}
            cardClass={cardClass}
            cardContentClass={cardContentClass}
          />
        }
      />
      <Divider />
      <CardContent className={cardContentClass}>
        <TableContainer component={Paper}>
          <Table aria-label="common roles table">
            <TableHead>
              <TableRow>
                <TableCell>Role Name</TableCell>
                <TableCell>Enabled</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Tooltip title="The Compute User role is designed to allow operations on EC2 instances, Step Functions, and invoking existing Lambda functions.">
                    <Link
                      underline="always"
                      style={{
                        textDecorationStyle: 'dashed',
                        textDecorationLine: 'underline',
                      }}
                    >
                      Compute User
                    </Link>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {commonRoles?.compute_user_enabled ? 'Enabled' : 'Disabled'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Tooltip title="The Data Scientist role is designed to allow access to SageMaker, Athena, Glue, and S3 objects.">
                    <Link
                      underline="always"
                      style={{
                        textDecorationStyle: 'dashed',
                        textDecorationLine: 'underline',
                      }}
                    >
                      Data Scientist role
                    </Link>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {commonRoles?.data_scientist_enabled ? 'Enabled' : 'Disabled'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Tooltip title="The Database User role is designed to allow operations on RDS, DynamoDB, and ElastiCache.">
                    <Link
                      underline="always"
                      style={{
                        textDecorationStyle: 'dashed',
                        textDecorationLine: 'underline',
                      }}
                    >
                      Database User
                    </Link>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {commonRoles?.database_user_enabled ? 'Enabled' : 'Disabled'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Tooltip title="The S3 Bucket User role is designed to allow operations on S3 objects, S3 batch jobs, and invoking existing Lambda functions.">
                    <Link
                      underline="always"
                      style={{
                        textDecorationStyle: 'dashed',
                        textDecorationLine: 'underline',
                      }}
                    >
                      S3 Bucket User
                    </Link>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {commonRoles?.s3_bucket_user_enabled ? 'Enabled' : 'Disabled'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
      <CardContent>
        <Typography variant="body1">
          For more information on Common Roles see{'  '}
          <Link
            target="_blank"
            href="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/infrastructure/aws/foundation-access-baseline/"
          >
            AWS Federated Access Overview
          </Link>{' '}
        </Typography>
      </CardContent>
    </Card>
  );
}
